/**
 * App routes — role-gated tree.
 *
 * Public:        /signup, /signin, /confirm
 * Customer:      /dashboard
 * Tenant Admin:  /admin/queue, /admin/catalog, /admin/packages, /admin/users
 * Platform Admin /admin/platform/tenants
 *
 * Role mismatches redirect to that role's landing page (FR-006).
 */

import { Navigate, Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useAuthRole, useAuthStatus } from "@/lib/auth/useAuth";

import { ConfirmSignUp } from "./confirm";
import { Dashboard } from "./dashboard";
import { ForgotPassword } from "./forgot-password";
import { EstimateWorkspace } from "./estimates/$estimateId";
import { NewEstimate } from "./estimates/new";
import { RootLayout } from "./_root";
import { SignIn } from "./signin";
import { SignUp } from "./signup";
import { TenantNotFound } from "./tenant-not-found";
import { AdminCatalog } from "./admin/catalog";
import { AdminEstimateReview } from "./admin/estimates/$estimateId";
import { AdminPackages } from "./admin/packages";
import { AdminQueue } from "./admin/queue";
import { AdminUsers } from "./admin/users";
import { PlatformTenants } from "./admin/platform/tenants";

const ROLE_LANDING: Record<string, string> = {
  customer: "/dashboard",
  tenant_admin: "/admin/queue",
  platform_admin: "/admin/platform/tenants",
};

function RootRedirect() {
  const status = useAuthStatus();
  const role = useAuthRole();
  if (status === "unknown") return null;
  if (status !== "authenticated" || !role) return <Navigate to="/signin" replace />;
  return <Navigate to={ROLE_LANDING[role] ?? "/dashboard"} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<RootRedirect />} />

        {/* Public */}
        <Route path="/signup" element={<SignUp />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/confirm" element={<ConfirmSignUp />} />
        <Route path="/tenant-not-found" element={<TenantNotFound />} />

        {/* Customer */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estimates/new"
          element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <NewEstimate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/estimates/:estimateId"
          element={
            <ProtectedRoute allowedRoles={["customer", "tenant_admin"]}>
              <EstimateWorkspace />
            </ProtectedRoute>
          }
        />

        {/* Tenant Admin */}
        <Route
          path="/admin/queue"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <AdminQueue />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/catalog"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <AdminCatalog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/packages"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <AdminPackages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin"]}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/estimates/:estimateId"
          element={
            <ProtectedRoute allowedRoles={["tenant_admin", "platform_admin"]}>
              <AdminEstimateReview />
            </ProtectedRoute>
          }
        />

        {/* Platform Admin */}
        <Route
          path="/admin/platform/tenants"
          element={
            <ProtectedRoute allowedRoles={["platform_admin"]}>
              <PlatformTenants />
            </ProtectedRoute>
          }
        />

        {/* Catch-all → root redirect */}
        <Route path="*" element={<RootRedirect />} />
      </Route>
    </Routes>
  );
}
